import React, { ChangeEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

import Typography from 'kingpin/atoms/Typography';

const Field = styled.label<{ $isActive: boolean; $hasError: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0px 16px;
  border-radius: 10px;
  cursor: text;
  border: 1px solid
    ${(props) => {
      if (props.$hasError) {
        return 'var(--color-status-error)';
      }
      return props.$isActive
        ? 'var(--color-info-v2)'
        : 'var(--color-border-primary)';
    }};
  background-color: ${(props) =>
    props.$isActive ? 'var(--color-bg-selected)' : 'var(--color-bg-primary)'};
  box-shadow: ${(props) =>
    props.$isActive && !props.$hasError
      ? '0px 0px 0px 1px var(--color-info-v2)'
      : 'none'};
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:hover,
  &:focus-within {
    border-color: ${(props) =>
      props.$hasError ? 'var(--color-status-error)' : 'var(--color-info-v2)'};
  }
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);

  &::placeholder {
    font-size: 14px;
    font-weight: 400;
    color: var(--color-text-muted);
  }
`;

// Up to two decimal places, as the backend keeps whole cents.
const AMOUNT_PATTERN = /^\d*(\.\d{0,2})?$/;

// A dollar amount entry styled to sit with the preset amount tiles: a "$"
// prefix, no native spinner, and the tiles' selected look while it holds the
// chosen amount.
const CustomAmountInput = ({
  value,
  setValue,
  placeholder,
  hasError,
  isAutoFocused,
  testId,
}: {
  value: number | undefined;
  setValue: (value: number | undefined) => void;
  placeholder: string;
  hasError: boolean;
  // Focus on mount, e.g. when revealed by picking "Other".
  isAutoFocused?: boolean;
  testId: string;
}) => {
  const [text, setText] = useState<string>(
    value === undefined ? '' : `${value}`,
  );

  // Picking a preset clears the custom amount.
  useEffect(() => {
    if (value === undefined) {
      setText('');
    }
  }, [value]);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value.replace(/[$,\s]/g, '');
    if (!AMOUNT_PATTERN.test(next)) {
      return;
    }
    setText(next);
    const parsed = Number.parseFloat(next);
    setValue(next === '' || Number.isNaN(parsed) ? undefined : parsed);
  };

  return (
    <Field $isActive={value !== undefined} $hasError={hasError}>
      <Typography.Header
        type={'H4'}
        color={
          value !== undefined
            ? 'var(--color-text-primary)'
            : 'var(--color-text-muted)'
        }
      >
        {'$'}
      </Typography.Header>
      <Input
        value={text}
        onChange={onChange}
        inputMode={'decimal'}
        autoFocus={isAutoFocused}
        placeholder={placeholder}
        data-testid={testId}
      />
    </Field>
  );
};

export default CustomAmountInput;
